// frontend/src/pages/dashboard/Taskhooks/useTaskTimer.js

import { useState, useEffect, useCallback } from "react";
import { timeEntryAPI } from "../../../services/api";
import { toast } from "react-toastify";
import realtime from "../../../hooks/RealtimePoll";

export const useTaskTimer = (authUser, setTasks) => {
  const [isTracking, setIsTracking] = useState(false);
  const [currentTimeEntryId, setCurrentTimeEntryId] = useState(null);
  const [timeEntryOwner, setTimeEntryOwner] = useState(null);
  const [ownerName, setOwnerName] = useState("");

  // Timer Tick - Update running timers every second
  useEffect(() => {
    const tick = setInterval(() => {
      setTasks((prevTasks) =>
        prevTasks.map((task) => {
          const tt = task.tracked_time || {};
          const isRunning = !!tt.is_running;
          const total = Number(tt.total_seconds || tt.totalSeconds || 0);
          if (!isRunning) return task;
          const newTotal = total + 1;
          const hours = Math.floor(newTotal / 3600);
          const mins = Math.floor((newTotal % 3600) / 60);
          const formatted = `${String(hours).padStart(2, "0")}h ${String(mins).padStart(2, "0")}m`;
          return {
            ...task,
            tracked_time: {
              ...tt,
              total_seconds: newTotal,
              formatted,
              is_running: true,
            },
          };
        })
      );
    }, 1000);
    return () => clearInterval(tick);
  }, [setTasks]);

  const startTimer = useCallback(async (currentTask) => {
    if (!currentTask) return;
    try {
      const startRes = await timeEntryAPI.start({ task_id: currentTask.id });
      const showRes = await timeEntryAPI.show(startRes.data.id);
      const taskIdFromRes = showRes.data.task_id;
      
      setTasks((prev) =>
        prev.map((t) =>
          Number(t.id) === Number(taskIdFromRes)
            ? { ...t, tracked_time: showRes.data.tracked_time }
            : t
        )
      );
      setIsTracking(true);
      setCurrentTimeEntryId(showRes.data.id);
      setTimeEntryOwner(authUser.id);
      setOwnerName(authUser.name);
      toast.success("⏱️ Timer started");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to start timer");
    }
  }, [authUser, setTasks]);

  const pauseTimer = useCallback(async () => {
    if (!currentTimeEntryId) return;
    try {
      await timeEntryAPI.pause(currentTimeEntryId);
      const res = await timeEntryAPI.show(currentTimeEntryId);
      const taskIdFromRes = res.data?.task_id ?? res.data?.task?.id;
      
      setTasks((prev) =>
        prev.map((t) =>
          Number(t.id) === Number(taskIdFromRes)
            ? { ...t, tracked_time: res.data.tracked_time }
            : t
        )
      );
      setIsTracking(false);
      realtime.advanceLastCheck("tasks");
      toast.info("⏸️ Timer paused");
    } catch (err) {
      toast.error("Failed to pause timer");
    }
  }, [currentTimeEntryId, setTasks]);

  const resumeTimer = useCallback(async () => {
    if (!currentTimeEntryId) return;
    try {
      await timeEntryAPI.resume(currentTimeEntryId);
      const res = await timeEntryAPI.show(currentTimeEntryId);
      const taskIdFromRes = res.data?.task_id ?? res.data?.task?.id;
      
      setTasks((prev) =>
        prev.map((t) =>
          Number(t.id) === Number(taskIdFromRes)
            ? { ...t, tracked_time: res.data.tracked_time }
            : t
        )
      );
      setIsTracking(true);
      realtime.advanceLastCheck("tasks");
      toast.info("▶️ Timer resumed");
    } catch (err) {
      toast.error("Failed to resume timer");
    }
  }, [currentTimeEntryId, setTasks]);

  const stopTimer = useCallback(async () => {
    if (!currentTimeEntryId) return;
    try {
      await timeEntryAPI.stop(currentTimeEntryId);
      const res = await timeEntryAPI.show(currentTimeEntryId);
      const taskIdFromRes = res.data?.task_id ?? res.data?.task?.id;
      
      setTasks((prev) =>
        prev.map((t) =>
          Number(t.id) === Number(taskIdFromRes)
            ? { ...t, tracked_time: res.data.tracked_time }
            : t
        )
      );
      setIsTracking(false);
      setCurrentTimeEntryId(null);
      setTimeEntryOwner(null);
      setOwnerName("");
      realtime.advanceLastCheck("tasks");
      toast.success("⏹️ Timer stopped");
    } catch (err) {
      toast.error("Failed to stop timer");
    }
  }, [currentTimeEntryId, setTasks]);

  const checkActiveTimer = useCallback(async (taskId) => {
    if (!taskId) return;
    try {
      const res = await timeEntryAPI.listByTask(taskId);
      const activeEntry = res.data.find(
        (e) => !e.end_time && e.tracked_time?.is_running
      );
      if (activeEntry) {
        setIsTracking(activeEntry.user_id === authUser.id);
        setCurrentTimeEntryId(activeEntry.id);
        setTimeEntryOwner(activeEntry.user_id);
        setOwnerName(activeEntry.user?.name || "User");
        setTasks((prev) =>
          prev.map((t) =>
            t.id === taskId
              ? { ...t, tracked_time: activeEntry.tracked_time }
              : t
          )
        );
        return;
      }
      const pausedEntry = res.data.find(
        (e) => !e.end_time && !e.tracked_time?.is_running
      );
      if (pausedEntry) {
        setIsTracking(false);
        setCurrentTimeEntryId(pausedEntry.id);
        setTimeEntryOwner(pausedEntry.user_id);
        setOwnerName(pausedEntry.user?.name || "User");
        setTasks((prev) =>
          prev.map((t) =>
            t.id === taskId
              ? { ...t, tracked_time: pausedEntry.tracked_time }
              : t
          )
        );
        return;
      }
      setIsTracking(false);
      setCurrentTimeEntryId(null);
      setTimeEntryOwner(null);
      setOwnerName("");
    } catch (err) {
      console.error("Timer check failed:", err);
    }
  }, [authUser, setTasks]);

  return {
    isTracking,
    currentTimeEntryId,
    timeEntryOwner,
    ownerName,
    startTimer,
    pauseTimer,
    resumeTimer,
    stopTimer,
    checkActiveTimer,
    setIsTracking,
    setCurrentTimeEntryId,
  };
};